// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { getContentByVersionPath } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import loadCatalogPageByPath from "@insite/client-framework/Store/Data/CatalogPages/Handlers/LoadCatalogPageByPath";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkProps } from "@insite/mobius/Link";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname === getContentByVersionPath
            ? parseQueryString<{ categoryPath: string }>(location.search).categoryPath
            : "") ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);

    return {
        catalogPageState,
        category: getCategoryState(
            state,
            catalogPageState.value?.categoryIdWithBrandId ?? catalogPageState.value?.categoryId,
        ).value,
        categoryPath,
        breadcrumbLinks: state.components.breadcrumbs.links,
        location: getLocation(state),
        page: getCurrentPage(state),
    };
};

const mapDispatchToProps = {
    loadCatalogPageByPath,
    setBreadcrumbs,
};

type OwnProps = {
    isPDP?: boolean;
    manufacturerItem?: string;
    categoryPathProp?: string;
    postFixBreadcrumb?: LinkProps;
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

class CurrentCategory extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { categoryPath, catalogPageState, manufacturerItem, isPDP, breadcrumbLinks } = this.props;
        if (!catalogPageState.value) {
            if (categoryPath && !catalogPageState.isLoading && !this.isPageUrlSegmentIsEqualToPathname()) {
                this.props.loadCatalogPageByPath({ path: categoryPath });
            }
        } else if (
            /**
             * XNG-Changes: if last breadCrumb, use manufacturerItem instead of text (product short description)
             * see setBreadcrumbs method below
             */
            breadcrumbLinks
                ?.slice(1)
                ?.map(o => o.children?.toString())
                .join(",") !==
                catalogPageState.value.breadCrumbs
                    ?.slice(1)
                    ?.map(o => o.text)
                    .join(",") ||
            (breadcrumbLinks?.length && breadcrumbLinks[0].children !== translate("Home_Breadcrumb"))
        ) {
            this.setBreadcrumbs();
        }
    }

    componentDidUpdate(prevProps: Props): void {
        const { categoryPath, catalogPageState, manufacturerItem } = this.props;
        if (!catalogPageState.value) {
            if (categoryPath && !catalogPageState.isLoading && !this.isPageUrlSegmentIsEqualToPathname()) {
                this.props.loadCatalogPageByPath({ path: categoryPath });
            }
        } else if (
            catalogPageState.value.breadCrumbs &&
            (prevProps.catalogPageState.value !== catalogPageState.value ||
                !this.props.breadcrumbLinks ||
                prevProps.manufacturerItem !== manufacturerItem)
        ) {
            this.setBreadcrumbs();
        }
    }

    isPageUrlSegmentIsEqualToPathname(): boolean {
        const pathname = this.props.location.pathname.toLowerCase();
        const urlSegments = this.props.page.translatableFields["urlSegment"];

        if (urlSegments) {
            for (const key in urlSegments) {
                let path = urlSegments[key].toLowerCase();
                if (path.indexOf("/") !== 0) {
                    path = `/${path}`;
                }
                if (pathname === path) {
                    return true;
                }
            }
        }

        return false;
    }

    componentWillUnmount() {
        this.props.setBreadcrumbs({ links: undefined });
    }

    setBreadcrumbs() {
        let links: LinkProps[] = this.props.catalogPageState.value!.breadCrumbs!.map(o => ({
            children: o.text,
            href: o.url,
        }));

        const { manufacturerItem, isPDP, postFixBreadcrumb } = this.props;
        if (links) {
            const lastIndex = links.length - 1;
            if (isPDP && manufacturerItem && links) {
                links = [...links];
                links[lastIndex] = { ...links[lastIndex], children: manufacturerItem };
            }
            if (postFixBreadcrumb) {
                links[lastIndex].href = links[lastIndex].href || this.props.categoryPathProp;
                links = [...links, postFixBreadcrumb];
            }
        }

        this.props.setBreadcrumbs({
            links,
        });
    }

    render() {
        if (!this.props.catalogPageState.value) {
            return this.props.children;
        }

        return <CategoryContext.Provider value={this.props.category}>{this.props.children}</CategoryContext.Provider>;
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(CurrentCategory);
